import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ExternalLink, LogIn, Phone, Mail, Clock, CheckCircle2, Building2, Briefcase } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import SiteNoticeBar from './SiteNoticeBar';

// Check if currently within phone hours (Mon-Fri 9:00-14:00)
const isPhoneHoursActive = () => {
  const now = new Date();
  const day = now.getDay(); // 0 = Sunday, 1 = Monday, ..., 5 = Friday, 6 = Saturday
  const hour = now.getHours();
  
  // Monday to Friday (1-5) and between 9:00 and 14:00
  const isWeekday = day >= 1 && day <= 5;
  const isWithinHours = hour >= 9 && hour < 14;
  
  return isWeekday && isWithinHours;
};

const KarriereCta = ({ testId }) => (
  <Link
    to="/karriere"
    data-testid={testId}
    className="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full bg-brand-gold/20 border border-brand-gold/50 text-brand-gold hover:bg-brand-gold hover:text-brand-navy transition-colors font-bold text-[13px] tracking-wide whitespace-nowrap"
  >
    <span className="w-1.5 h-1.5 rounded-full bg-brand-gold animate-pulse" />
    <Briefcase className="w-3.5 h-3.5" />
    <span>Stellenangebote</span>
  </Link>
);


const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [visible, setVisible] = useState(true);
  const [phoneAvailable, setPhoneAvailable] = useState(isPhoneHoursActive());
  const [showInfoPopup, setShowInfoPopup] = useState(false);
  const popupRef = useRef(null);
  const lastScrollY = useRef(0);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Determine if scrolled past threshold
      setScrolled(currentScrollY > 20);
      
      // Show header when scrolling up, hide when scrolling down
      if (currentScrollY < 100) {
        // Always show at top of page
        setVisible(true);
      } else if (currentScrollY < lastScrollY.current) {
        // Scrolling UP - show header
        setVisible(true);
      } else if (currentScrollY > lastScrollY.current + 10) {
        // Scrolling DOWN (with 10px threshold to avoid jitter) - hide header
        setVisible(false);
      }
      
      lastScrollY.current = currentScrollY;
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Update phone availability every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setPhoneAvailable(isPhoneHoursActive());
    }, 60000); // Check every minute
    return () => clearInterval(interval);
  }, []);

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setShowInfoPopup(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navLinks = [
    { name: 'Startseite', path: '/' },
    { name: 'WEG-Verwaltung', path: '/weg-verwaltung' },
    { name: 'Mietverwaltung', path: '/mietverwaltung' },
    { name: 'Sondereigentum', path: '/sondereigentumsverwaltung' },
    { name: 'Referenzen', path: '/referenzen' },
    { name: 'Kontakt', path: '/kontakt' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav 
      data-testid="main-navbar" 
      className={`fixed top-0 left-0 right-0 z-[9999] transition-transform duration-300 pt-[env(safe-area-inset-top)] bg-brand-navy ${
        visible ? 'translate-y-0' : '-translate-y-full'
      }`}
    >
      {/* ─── Temporärer Hinweis-Banner (ganz oben, schaltbar im Admin) ─── */}
      <SiteNoticeBar />

      {/* ─── Top Bar ─── Always visible, doesn't hide on scroll */}
      <div className="bg-brand-navy">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-10">
          {/* Left: Contact Info */}
          <div className="hidden md:flex items-center gap-5 text-white/70 text-[13px] whitespace-nowrap">
            <a href="tel:+49611603313" className="flex items-center gap-1.5 hover:text-brand-gold transition-colors">
              <Phone className="w-3.5 h-3.5" />
              0611 - 60 33 13
            </a>
            <Link to="/kontakt" className="flex items-center gap-1.5 hover:text-brand-gold transition-colors">
              <Mail className="w-3.5 h-3.5" />
              Kontakt
            </Link>
            {phoneAvailable ? (
              <div 
                className="relative hidden xl:block" 
                ref={popupRef}
                onMouseEnter={() => setShowInfoPopup(true)}
                onMouseLeave={() => setShowInfoPopup(false)}
              >
                <span 
                  className="flex items-center gap-1.5 text-green-400 hover:text-green-300 transition-colors cursor-pointer"
                >
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Jetzt telefonisch erreichbar
                </span>
                
                {/* Info Popup */}
                <AnimatePresence>
                  {showInfoPopup && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute top-full left-0 mt-2 w-72 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-[9999]"
                    >
                      <div className="p-4">
                        <h4 className="font-semibold text-brand-navy text-sm mb-3">Erreichbarkeit</h4>
                        
                        <div className="space-y-3">
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
                              <Phone className="w-4 h-4 text-green-600" />
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-800">Telefonische Sprechzeiten</p>
                              <p className="text-xs text-gray-500">Mo – Fr: 09:00 – 14:00 Uhr</p>
                              <div className="mt-1 space-y-0.5">
                                <div className="flex items-center gap-2">
                                  <a href="tel:+49611603313" className="text-xs text-brand-gold font-medium">0611 - 60 33 13</a>
                                  <span className="text-[10px] text-gray-400">(Wiesbaden)</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <a href="tel:+4961342863266" className="text-xs text-brand-gold font-medium">06134 - 28 63 266</a>
                                  <span className="text-[10px] text-gray-400">(Kostheim)</span>
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                              <Building2 className="w-4 h-4 text-blue-600" />
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-800">Bürozeiten</p>
                              <p className="text-xs text-gray-500">Mo – Fr: 09:00 – 17:00 Uhr</p>
                            </div>
                          </div>
                          
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-lg bg-brand-gold/10 flex items-center justify-center flex-shrink-0">
                              <Clock className="w-4 h-4 text-brand-gold" />
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-800">Kundenportal</p>
                              <p className="text-xs text-gray-500">Jederzeit verfügbar</p>
                            </div>
                          </div>
                        </div>
                        
                        <Link 
                          to="/login" 
                          className="mt-4 w-full block text-center py-2 bg-brand-navy text-white text-xs font-medium rounded-lg hover:bg-brand-navy-dark transition-colors"
                        >
                          Zum Kundenportal
                        </Link>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <Link to="/login" className="hidden xl:flex items-center gap-1.5 hover:text-brand-gold transition-colors">
                <Clock className="w-3.5 h-3.5" />
                Jederzeit im Portal für Sie erreichbar
              </Link>
            )}
          </div>

          {/* Karriere CTA: bis lg links im Fluss, ab lg absolut zentriert (zwei Instanzen, da lg:absolute im Build nicht zuverlässig greift) */}
          <div className="lg:hidden flex-shrink-0 md:ml-5 z-10">
            <KarriereCta testId="karriere-top-btn" />
          </div>
          <div className="hidden lg:block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
            <KarriereCta testId="karriere-top-btn-desktop" />
          </div>

          {/* Right: Quick Links */}
          <div className="ml-auto flex items-center gap-4 text-[13px] whitespace-nowrap">
            <a
              href="https://hvscholze-kurz.mycasavi.com/app/login"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="casavi-portal-btn"
              className="hidden md:flex items-center gap-1.5 text-white/70 hover:text-brand-gold transition-colors"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              casavi
            </a>
            <Link
              to="/login"
              data-testid="login-btn"
              className="flex items-center gap-2 text-brand-gold hover:text-brand-gold-light transition-colors font-semibold text-[15px]"
            >
              <LogIn className="w-4 h-4" />
              Kundenportal
            </Link>
          </div>
        </div>
      </div>

      {/* ─── Main Bar ─── */}
      <div className={`bg-white transition-all duration-300 ${scrolled ? 'shadow-md' : 'shadow-sm'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 sm:h-20 lg:h-24">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 sm:gap-3 lg:gap-4 flex-shrink-0 self-center" data-testid="logo-link">
              <img 
                src="/images/logo.png" 
                alt="Logo Scholze-Kurz & Kurz Immobilien GmbH – Hausverwaltung Wiesbaden Mainz, WEG- und Mietverwaltung im Rhein-Main-Gebiet" 
                title="Scholze-Kurz & Kurz Immobilien GmbH – Hausverwaltung seit 1981"
                width="160"
                height="48"
                className="h-8 sm:h-10 lg:h-12 w-auto"
              />
              <div className="leading-none pt-2 sm:pt-2.5 lg:pt-3">
                <span className="text-brand-navy text-[9px] sm:text-[11px] lg:text-sm font-semibold tracking-[0.08em] sm:tracking-[0.12em] uppercase block">
                  Hausverwaltung
                </span>
                <div className="text-brand-navy font-bold text-lg sm:text-xl lg:text-[24px] leading-tight -mt-0.5">
                  Scholze-Kurz & Kurz
                </div>
                <span className="text-brand-gold text-[8px] sm:text-[9px] lg:text-[11px] font-medium hidden xs:block">
                  ...seit 1981
                </span>
              </div>
            </Link>

            {/* Desktop Navigation – aligned to bottom to match company name baseline */}
            <div className="hidden lg:flex items-end pb-5 gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
                  className={`px-3 xl:px-4 py-2 text-[14px] xl:text-[15px] font-medium transition-colors whitespace-nowrap ${
                    isActive(link.path) 
                      ? 'text-brand-gold' 
                      : 'text-brand-navy hover:text-brand-gold'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* Mobile: Phone + Menu */}
            <div className="flex items-center gap-2 lg:hidden self-center">
              <a
                href="tel:+49611603313"
                className="p-2.5 rounded-lg text-brand-navy hover:bg-gray-100 transition-colors"
                aria-label="Anrufen"
              >
                <Phone className="w-5 h-5" />
              </a>
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="p-2.5 -mr-1 rounded-lg text-brand-navy hover:bg-gray-100 active:bg-gray-200 transition-colors"
                data-testid="mobile-menu-btn"
                aria-label="Menu"
              >
                {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ─── Mobile Menu ─── */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-gray-100 shadow-xl"
            data-testid="mobile-menu"
          >
            <div className="px-4 py-3 space-y-0.5">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className={`block px-4 py-3.5 rounded-xl text-base font-medium transition-colors active:scale-[0.98] ${
                    isActive(link.path)
                      ? 'bg-brand-navy/5 text-brand-gold'
                      : 'text-brand-navy hover:bg-gray-50 active:bg-gray-100'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <hr className="my-2 border-gray-100" />
              <a
                href="tel:+49611603313"
                className="flex items-center gap-3 px-4 py-3.5 text-base font-medium text-brand-navy bg-gray-50 rounded-xl active:bg-gray-100"
              >
                <Phone className="w-5 h-5" />
                0611 - 60 33 13
              </a>
              <a
                href="https://hvscholze-kurz.mycasavi.com/app/login"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-3.5 text-base font-medium text-brand-navy hover:bg-gray-50 rounded-xl active:bg-gray-100"
              >
                WEG-Kundenportal
                <ExternalLink className="w-4 h-4" />
              </a>
              <Link
                to="/login"
                onClick={() => setIsOpen(false)}
                className="flex items-center justify-center gap-2 px-4 py-3.5 mt-1 text-base font-semibold bg-brand-navy text-white rounded-xl active:bg-brand-navy-dark"
              >
                <LogIn className="w-5 h-5" />
                Kundenportal
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
